import type { TaskRow } from '../../state/taskRows';

export type AgentBarScenarioGroup = 'Core flow' | 'Questions' | 'Reviews' | 'Outcomes';

export interface AgentBarScenario {
  id: string;
  label: string;
  description: string;
  group: AgentBarScenarioGroup;
  rows: TaskRow[];
  initialState?: {
    text?: string;
  };
}

const PRIMARY_PREVIEW_KEY = 'agent-bar-preview-primary';
const QUEUED_PREVIEW_KEY = 'agent-bar-preview-queue';
const strategyPage = { sectionId: 'strategy', number: 4, title: 'Strategy' };
const audiencePage = { sectionId: 'audience_profiles', number: 5, title: 'Audience' };

function previewImage(label: string, start: string, end: string, accent: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="600" viewBox="0 0 480 600">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="${start}"/><stop offset="1" stop-color="${end}"/></linearGradient></defs>
    <rect width="480" height="600" fill="url(#g)"/>
    <circle cx="370" cy="135" r="126" fill="${accent}" opacity=".58"/>
    <circle cx="92" cy="480" r="174" fill="#fff" opacity=".11"/>
    <path d="M-20 410C110 300 205 520 500 325V620H-20Z" fill="#090706" opacity=".3"/>
    <text x="34" y="546" fill="#fff" font-family="Arial,sans-serif" font-size="26" font-weight="700">${label}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function row(overrides: Partial<TaskRow>): TaskRow {
  return {
    key: PRIMARY_PREVIEW_KEY,
    state: 'running',
    request: 'Make the proposal more focused for the summer campaign.',
    text: '',
    ask: null,
    summary: [],
    undo: false,
    versionAtSend: 7,
    error: null,
    stopped: false,
    activeTool: null,
    status: null,
    page: null,
    kind: null,
    summaryLine: null,
    ...overrides,
  };
}

/**
 * Real AgentBar permutations used by the in-product dev lab. The primary row
 * keeps one key across scenarios so React exercises the same running -> ask ->
 * done transition path as a live task instead of remounting a showcase mock.
 */
export const AGENT_BAR_SCENARIOS: AgentBarScenario[] = [
  {
    id: 'default',
    label: 'Default',
    description: 'No activity. Only the always-available request field remains.',
    group: 'Core flow',
    rows: [],
  },
  {
    id: 'draft-input',
    label: 'Draft in input',
    description: 'A realistic multi-line request checks field growth and send affordance.',
    group: 'Core flow',
    rows: [],
    initialState: {
      text: 'Tighten the strategy page and make the audience rationale easier to scan.',
    },
  },
  {
    id: 'thinking',
    label: 'Running',
    description: 'A task is active with a live status line, page context, aura, and Stop.',
    group: 'Core flow',
    rows: [row({ status: { label: 'Thinking through the request…' }, page: strategyPage })],
  },
  {
    id: 'running-with-queue',
    label: 'Running + queued',
    description: 'A new instruction waits below the active task while the input stays available.',
    group: 'Core flow',
    rows: [
      row({ status: { label: 'Finding images…' }, page: audiencePage }),
      row({
        key: QUEUED_PREVIEW_KEY,
        state: 'queued',
        request: 'Then shorten the supporting audience copy.',
        page: audiencePage,
        versionAtSend: null,
      }),
    ],
  },
  {
    id: 'pill-question',
    label: 'Quick choice',
    description: 'Two short answers render as pills with one recommended action.',
    group: 'Questions',
    rows: [row({
      state: 'waiting',
      ask: {
        id: 'preview-pill-ask',
        question: 'Which tone should I use?',
        allowLater: true,
        recommended: 0,
        answers: [{ label: 'Confident' }, { label: 'Playful' }],
      },
    })],
  },
  {
    id: 'row-question',
    label: 'Detailed question',
    description: 'Longer answers move into full-width rows and retain page context.',
    group: 'Questions',
    rows: [row({
      state: 'waiting',
      page: strategyPage,
      ask: {
        id: 'preview-row-ask',
        question: 'What should the campaign prioritize?',
        allowLater: true,
        answers: [
          { label: 'Drive qualified leads as efficiently as possible' },
          { label: 'Build awareness before asking for conversion' },
          { label: 'Balance immediate demand with long-term growth' },
        ],
      },
    })],
  },
  {
    id: 'image-question',
    label: 'Image picker',
    description: 'Four image options exercise tile selection and the confirm action.',
    group: 'Questions',
    rows: [row({
      state: 'waiting',
      text: 'Choose an image direction for this page.',
      page: audiencePage,
      ask: {
        id: 'preview-image-ask',
        question: 'Which image direction feels right?',
        allowLater: true,
        answers: [
          { label: 'Warm audience', imageUrl: previewImage('WARM', '#b85f3d', '#4b2430', '#f3ae72') },
          { label: 'High intent', imageUrl: previewImage('INTENT', '#332662', '#17162f', '#8e67d8') },
          { label: 'Human strategy', imageUrl: previewImage('HUMAN', '#2c5960', '#162b35', '#63c0ad') },
          { label: 'Comfort at home', imageUrl: previewImage('COMFORT', '#7b5945', '#2e2729', '#dc9b68') },
        ],
      },
    })],
  },
  {
    id: 'contextual-review',
    label: 'Slide review question',
    description: 'Clicking a slide review badge opens that slide and presents one decision in the AgentBar.',
    group: 'Reviews',
    rows: [row({
      state: 'waiting',
      text: "Gifting intent converts online as well as in-store, so the current audience includes a wider Hudson Valley and NY-metro shipping tail.",
      page: audiencePage,
      ask: {
        id: 'preview-contextual-review',
        question: 'Should Occasion Gift Buyers stay inside the 20-mile radius, or include regional online gift shoppers?',
        allowLater: false,
        recommended: 1,
        answers: [
          { label: '20-mile radius only' },
          { label: 'Include regional shipping' },
        ],
      },
      dismissible: false,
    })],
  },
  {
    id: 'completed-change',
    label: 'Completed change',
    description: 'A saved proposal update offers Undo and reports the changed page.',
    group: 'Outcomes',
    rows: [row({
      state: 'done',
      page: strategyPage,
      kind: 'change',
      summary: ['Tightened the strategy and aligned its supporting message.'],
      summaryLine: 'Tightened the strategy and aligned its supporting message.',
      undo: true,
    })],
  },
  {
    id: 'completed-change-with-reply',
    label: 'Completed change with reply',
    description: 'A saved update whose turn also ended with a reply for the rep stays open until Ok.',
    group: 'Outcomes',
    rows: [row({
      state: 'done',
      kind: 'change',
      summary: ['Created the brief at $10,000/mo, Los Angeles metro.'],
      summaryLine: 'Created the brief at $10,000/mo, Los Angeles metro.',
      text: 'Intake is done. One gap: I could not find their website — send me the URL and I will fill in the business details.',
      undo: true,
    })],
  },
  {
    id: 'completed-answer',
    label: 'Completed answer',
    description: 'A text-only response stays until the reviewer acknowledges it.',
    group: 'Outcomes',
    rows: [row({
      state: 'done',
      text: 'The plan puts most of the budget into high-intent search, then uses streaming video to widen qualified reach.',
      page: strategyPage,
      kind: 'answer',
    })],
  },
  {
    id: 'error',
    label: 'Error + retry',
    description: 'A failed request explains the problem and keeps the source instruction for Retry.',
    group: 'Outcomes',
    rows: [row({
      state: 'done',
      kind: 'error',
      error: 'I could not save the image change. The source file is no longer available.',
      page: audiencePage,
    })],
  },
  {
    id: 'stopped',
    label: 'Stopped',
    description: 'A stopped task reports its final state before the row closes automatically.',
    group: 'Outcomes',
    rows: [row({
      state: 'done',
      kind: 'stopped',
      stopped: true,
      summaryLine: 'Stopped.',
      page: strategyPage,
    })],
  },
];

export function agentBarScenarioAt(index: number | null): AgentBarScenario | null {
  return index === null ? null : AGENT_BAR_SCENARIOS[index] ?? null;
}
